import { Badge, Button, Input, cn } from '@sitewise/ui';
import { Check, Pencil, X } from 'lucide-react';
import { useEffect, useId, useState } from 'react';

import type { UnitFieldValue } from '../../../api/units.js';

interface CustomFieldRendererProps {
  field: UnitFieldValue;
  onSave: (rawValue: unknown) => Promise<void>;
  onClear: () => Promise<void>;
  readOnly?: boolean;
}

/**
 * Inline view + edit for a single custom-field row on the Overview
 * tab. The shape of the editor depends on `field.fieldType` —
 * boolean fields render a yes/no toggle, selects render checkboxes /
 * radios, and the rest fall back to a small input. Save/cancel logic
 * is owned here so the parent can remain a passive render target.
 */
export function CustomFieldRenderer({
  field,
  onSave,
  onClear,
  readOnly,
}: CustomFieldRendererProps) {
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function handleSave(raw: unknown) {
    setSaving(true);
    setError(null);
    try {
      await onSave(raw);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  }

  async function handleClear() {
    setSaving(true);
    setError(null);
    try {
      await onClear();
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not clear');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid grid-cols-[10rem_1fr] items-start gap-3 py-1.5">
      <div className="space-y-0.5 pt-1">
        <div className="text-xs font-medium uppercase tracking-wide text-fg-muted">
          {field.label}
        </div>
        {field.fieldType === 'long_text' ? null : null}
      </div>
      <div className="min-w-0 space-y-1">
        {readOnly ? (
          <div className="text-sm text-fg-secondary">
            <FieldValueDisplay field={field} />
          </div>
        ) : editing ? (
          <FieldEditor
            field={field}
            saving={saving}
            onSave={handleSave}
            onCancel={() => {
              setEditing(false);
              setError(null);
            }}
            onClear={field.value != null ? handleClear : undefined}
          />
        ) : (
          <button
            type="button"
            className={cn(
              'group flex max-w-full items-center gap-1.5 rounded-sm px-1.5 py-1 text-left text-sm transition-colors hover:bg-surface-muted',
              field.value == null ? 'text-fg-muted' : 'text-fg-secondary',
            )}
            onClick={() => setEditing(true)}
            aria-label={`Edit ${field.label}`}
          >
            <FieldValueDisplay field={field} placeholder />
            <Pencil
              className="h-3 w-3 shrink-0 text-fg-muted opacity-0 transition-opacity group-hover:opacity-100"
              aria-hidden
            />
          </button>
        )}
        {error ? <div className="text-xs text-danger">{error}</div> : null}
      </div>
    </div>
  );
}

function FieldValueDisplay({
  field,
  placeholder,
}: {
  field: UnitFieldValue;
  placeholder?: boolean;
}) {
  if (field.value == null) {
    return placeholder ? <span>Add value</span> : <span>—</span>;
  }
  switch (field.fieldType) {
    case 'yes_no':
      return <span>{field.value === true ? 'Yes' : 'No'}</span>;
    case 'date':
      return typeof field.value === 'string' ? (
        <span>{new Date(`${field.value}T00:00:00Z`).toLocaleDateString()}</span>
      ) : (
        <span>—</span>
      );
    case 'multi_select': {
      const values = Array.isArray(field.value) ? (field.value as string[]) : [];
      if (values.length === 0) return <span>—</span>;
      const labels = values.map(
        (v) => field.options?.find((o) => o.value === v)?.label ?? v,
      );
      return (
        <span className="flex flex-wrap gap-1">
          {labels.map((label) => (
            <Badge key={label} variant="outline" className="text-xs">
              {label}
            </Badge>
          ))}
        </span>
      );
    }
    case 'single_select': {
      const opt = field.options?.find((o) => o.value === field.value);
      return <span>{opt?.label ?? String(field.value)}</span>;
    }
    case 'number':
      return <span className="tabular-nums">{String(field.value)}</span>;
    default:
      return <span className="whitespace-pre-wrap">{String(field.value)}</span>;
  }
}

interface EditorProps {
  field: UnitFieldValue;
  saving: boolean;
  onSave: (raw: unknown) => Promise<void>;
  onCancel: () => void;
  onClear?: () => Promise<void>;
}

function FieldEditor({ field, saving, onSave, onCancel, onClear }: EditorProps) {
  const inputId = useId();
  const [text, setText] = useState(() => stringifyForEditing(field));
  const [bool, setBool] = useState<boolean>(() => field.value === true);
  const [selected, setSelected] = useState<string>(() =>
    typeof field.value === 'string' ? field.value : '',
  );
  const [multi, setMulti] = useState<string[]>(() =>
    Array.isArray(field.value) ? (field.value as string[]) : [],
  );

  // Reset draft state if the field rendered changes underneath us
  // (e.g. another tab edits the same field while editing here).
  useEffect(() => {
    setText(stringifyForEditing(field));
    setBool(field.value === true);
    setSelected(typeof field.value === 'string' ? field.value : '');
    setMulti(Array.isArray(field.value) ? (field.value as string[]) : []);
  }, [field]);

  async function commit() {
    if (saving) return;
    const raw = pickRaw(field, text, bool, selected, multi);
    if (raw === undefined) {
      // Empty values clear the field rather than saving "" / 0.
      if (onClear) await onClear();
      return;
    }
    await onSave(raw);
  }

  switch (field.fieldType) {
    case 'long_text':
      return (
        <div className="flex flex-col gap-1.5">
          <textarea
            id={inputId}
            aria-label={field.label}
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={saving}
            rows={3}
            className="rounded border border-border bg-surface px-2 py-1.5 text-sm"
          />
          <EditorButtons saving={saving} onSave={commit} onCancel={onCancel} onClear={onClear} />
        </div>
      );
    case 'yes_no':
      return (
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1 text-sm">
            <input
              type="radio"
              checked={bool}
              onChange={() => setBool(true)}
              disabled={saving}
            />
            Yes
          </label>
          <label className="inline-flex items-center gap-1 text-sm">
            <input
              type="radio"
              checked={!bool}
              onChange={() => setBool(false)}
              disabled={saving}
            />
            No
          </label>
          <EditorButtons saving={saving} onSave={commit} onCancel={onCancel} onClear={onClear} />
        </div>
      );
    case 'single_select':
      return (
        <div className="flex flex-wrap items-center gap-2">
          <select
            id={inputId}
            aria-label={field.label}
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={saving}
            className="rounded border border-border bg-surface px-2 py-1 text-sm"
          >
            <option value="">— Select —</option>
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <EditorButtons saving={saving} onSave={commit} onCancel={onCancel} onClear={onClear} />
        </div>
      );
    case 'multi_select':
      return (
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap gap-1.5">
            {field.options?.map((opt) => {
              const checked = multi.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className={cn(
                    'inline-flex cursor-pointer items-center gap-1 rounded-sm border border-border bg-surface px-2 py-1 text-xs',
                    checked && 'bg-primary-subtle text-primary',
                  )}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={checked}
                    onChange={() =>
                      setMulti((cur) =>
                        cur.includes(opt.value)
                          ? cur.filter((v) => v !== opt.value)
                          : [...cur, opt.value],
                      )
                    }
                    disabled={saving}
                  />
                  {opt.label}
                </label>
              );
            })}
          </div>
          <EditorButtons saving={saving} onSave={commit} onCancel={onCancel} onClear={onClear} />
        </div>
      );
    default:
      return (
        <div className="flex items-center gap-2">
          <Input
            id={inputId}
            aria-label={field.label}
            type={field.fieldType === 'number' ? 'number' : field.fieldType === 'date' ? 'date' : 'text'}
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={saving}
            className="h-7 px-2 py-1 text-sm"
          />
          <EditorButtons saving={saving} onSave={commit} onCancel={onCancel} onClear={onClear} />
        </div>
      );
  }
}

function EditorButtons({
  saving,
  onSave,
  onCancel,
  onClear,
}: {
  saving: boolean;
  onSave: () => Promise<void>;
  onCancel: () => void;
  onClear?: () => Promise<void>;
}) {
  return (
    <div className="flex items-center gap-1">
      <Button
        size="icon"
        variant="ghost"
        type="button"
        aria-label="Save"
        disabled={saving}
        onClick={() => void onSave()}
      >
        <Check className="h-3.5 w-3.5 text-success" />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        type="button"
        aria-label="Cancel"
        disabled={saving}
        onClick={onCancel}
      >
        <X className="h-3.5 w-3.5 text-fg-muted" />
      </Button>
      {onClear ? (
        <button
          type="button"
          disabled={saving}
          className="text-xs text-fg-muted hover:text-danger"
          onClick={() => void onClear()}
        >
          Clear
        </button>
      ) : null}
    </div>
  );
}

function stringifyForEditing(field: UnitFieldValue): string {
  if (field.value == null) return '';
  if (typeof field.value === 'number') return String(field.value);
  if (typeof field.value === 'string') return field.value;
  return '';
}

/** Convert the local editor state into the raw value the API wants.
 *  Returns `undefined` when the value should be cleared rather than
 *  saved (empty string for text/number/date is interpreted that way). */
function pickRaw(
  field: UnitFieldValue,
  text: string,
  bool: boolean,
  selected: string,
  multi: string[],
): unknown {
  switch (field.fieldType) {
    case 'yes_no':
      return bool;
    case 'number': {
      const trimmed = text.trim();
      if (trimmed === '') return undefined;
      const n = Number(trimmed);
      if (!Number.isFinite(n)) throw new Error('Enter a valid number');
      return n;
    }
    case 'date':
      return text.trim() === '' ? undefined : text.trim();
    case 'single_select':
      return selected === '' ? undefined : selected;
    case 'multi_select':
      return multi.length === 0 ? undefined : multi;
    default:
      return text === '' ? undefined : text;
  }
}
